import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { conversion } from "./InvoiceLineItems";

interface InvoiceLineItemsPrintViewProps {
  isCorePriceEnabled: boolean;
  isOrderInvoice: boolean;
  isChinaDealer: boolean;
  rows: any[];
  countryCurrency: string | undefined;
}

const InvoiceLineItemsPrintView = ({
  isCorePriceEnabled,
  isOrderInvoice,
  isChinaDealer,
  rows,
  countryCurrency,
}: InvoiceLineItemsPrintViewProps) => {
  const { t, i18n } = useTranslation("invoices");

  return (
    <TableContainer>
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell data-testId="lineNumber">
              {t("invoiceLineItems.header.lineNumber")}
            </TableCell>
            <TableCell align="left" data-testId="documentNumber">
              {t("invoiceLineItems.header.documentNumber")}
            </TableCell>
            {isOrderInvoice && (
              <>
                <TableCell align="left" data-testId="handlingUnit">
                  {t("invoiceLineItems.header.handlingUnit")}
                </TableCell>
                <TableCell align="left" data-testId="dealerReference">
                  {t("invoiceLineItems.header.dealerReference")}
                </TableCell>
              </>
            )}
            {isChinaDealer && isOrderInvoice && (
              <TableCell align="left" data-testId="deliveryNumber">
                {t("invoiceLineItems.header.deliveryNumber")}
              </TableCell>
            )}
            <TableCell align="left" data-testId="partNumber">
              {t("invoiceLineItems.header.partNumber")}
            </TableCell>
            <TableCell align="left" data-testId="description">
              {t("invoiceLineItems.header.description")}
            </TableCell>
            <TableCell align="left" data-testId="quantity">
              {t("invoiceLineItems.header.quantity")}
            </TableCell>
            <TableCell align="left" data-testId="unitPrice">
              {t("invoiceLineItems.header.unitPrice")}
            </TableCell>
            <TableCell align="left" data-testId="extendedDiscount">
              {t("invoiceLineItems.header.extendedDiscount")}
            </TableCell>
            <TableCell align="left" data-testId="surcharges">
              {t("invoiceLineItems.header.surcharges")}
            </TableCell>
            <TableCell align="left" data-testId="extendedAmount">
              {t("invoiceLineItems.header.extendedAmount")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.id}
              sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
            >
              <TableCell
                component="th"
                scope="row"
                data-testId={`${row.id}-lineNumber`}
              >
                {
                  // @ts-ignore
                  row.lineNumber || ""
                }
              </TableCell>
              <TableCell align="left" data-testId={`${row.id}-documentNumber`}>
                {
                  // @ts-ignore
                  row.documentNumber || ""
                }
              </TableCell>
              {isOrderInvoice && (
                <>
                  <TableCell
                    align="left"
                    data-testId={`${row.id}-handlingUnit`}
                  >
                    {row.handlingUnit ? (
                      <>
                        <Typography color="primary" data-fuel-size="sm">
                          {row.handlingUnit.number}
                        </Typography>
                        <Typography color="tertiary" data-fuel-size="sm">
                          ({row.handlingUnit.quantity}{" "}
                          {row.handlingUnit.quantityUnit})
                        </Typography>
                      </>
                    ) : (
                      ""
                    )}
                  </TableCell>
                  <TableCell
                    align="left"
                    data-testId={`${row.id}-dealerReference`}
                  >
                    {
                      // @ts-ignore
                      row.dealerReference || ""
                    }
                  </TableCell>
                </>
              )}
              {isChinaDealer && isOrderInvoice && (
                <TableCell
                  align="left"
                  data-testId={`${row.id}-deliveryNumber`}
                >
                  {
                    // @ts-ignore
                    row.deliveryNumber || ""
                  }
                </TableCell>
              )}
              <TableCell align="left" data-testId={`${row.id}-partNumber`}>
                {
                  // @ts-ignore
                  row.partNumber || ""
                }
              </TableCell>
              <TableCell align="left" data-testId={`${row.id}-description`}>
                {
                  // @ts-ignore
                  row.description || ""
                }
              </TableCell>
              <TableCell align="left" data-testId={`${row.id}-quantity`}>
                {
                  // @ts-ignore
                  row.quantity || ""
                }
              </TableCell>
              <TableCell align="left" data-testId={`${row.id}-unitPrice`}>
                <Grid>
                  <Grid>
                    {conversion(
                      row.unitPrice,
                      i18n.language,
                      countryCurrency
                    ) || ""}
                  </Grid>
                  {isCorePriceEnabled && row.coreUnitPrice > 0 && (
                    <Typography data-fuel-size="xs">
                      Core:{" "}
                      {conversion(
                        row.coreUnitPrice,
                        i18n.language,
                        countryCurrency
                      ) || ""}
                    </Typography>
                  )}
                </Grid>
              </TableCell>
              <TableCell
                align="left"
                data-testId={`${row.id}-extendedDiscount`}
              >
                {
                  // @ts-ignore
                  conversion(
                    row.extendedDiscount,
                    i18n.language,
                    countryCurrency
                  ) || ""
                }
              </TableCell>
              <TableCell align="left" data-testId={`${row.id}-freightCharges`}>
                {
                  // @ts-ignore
                  conversion(
                    row.freightCharges,
                    i18n.language,
                    countryCurrency
                  ) || ""
                }
              </TableCell>
              <TableCell align="left" data-testId={`${row.id}-extendedAmount`}>
                <Grid>
                  <Grid>
                    {conversion(
                      row.extendedAmount,
                      i18n.language,
                      countryCurrency
                    ) || ""}
                  </Grid>
                  {isCorePriceEnabled && row.coreUnitPrice > 0 && (
                    <Typography data-fuel-size="xs">
                      Core:{" "}
                      {conversion(
                        row.coreUnitPrice,
                        i18n.language,
                        countryCurrency
                      ) || ""}
                    </Typography>
                  )}
                </Grid>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default InvoiceLineItemsPrintView;
